import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import { supabase } from '../../config/supabase';
    import { useAuth } from '../../context/AuthContext';
    import SafeIcon from '../../common/SafeIcon';
    import JobFormModal from './JobFormModal';
    import * as FiIcons from 'react-icons/fi';

    const { FiPlus, FiEdit2, FiTrash2, FiClock, FiCheckCircle, FiAlertCircle } = FiIcons;

    const UserJobsTab = () => {
      const { user } = useAuth();
      const [jobs, setJobs] = useState([]);
      const [loading, setLoading] = useState(true);
      const [showFormModal, setShowFormModal] = useState(false);
      const [editingJob, setEditingJob] = useState(null);

      const fetchUserJobs = async () => {
        if (!user) return;
        try {
          setLoading(true);
          const { data, error } = await supabase
            .from('jobs')
            .select('*, profiles(name)')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
          if (error) throw error;
          setJobs(data || []);
        } catch (error) {
          console.error('Error fetching user jobs:', error);
        } finally {
          setLoading(false);
        }
      };

      useEffect(() => {
        fetchUserJobs();
      }, [user]);

      const handleAddNew = () => {
        setEditingJob(null);
        setShowFormModal(true);
      };

      const handleEdit = (job) => {
        setEditingJob(job);
        setShowFormModal(true);
      };

      const handleDelete = async (jobId) => {
        if (window.confirm('Are you sure you want to delete this job posting?')) {
          try {
            const { error } = await supabase.from('jobs').delete().eq('id', jobId);
            if (error) throw error;
            fetchUserJobs();
          } catch (error) {
            console.error('Error deleting job:', error);
          }
        }
      };
      
      const getStatusChip = (status) => {
        switch (status) {
          case 'approved':
            return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><SafeIcon icon={FiCheckCircle} className="mr-1.5" /> Approved</span>;
          case 'rejected':
            return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><SafeIcon icon={FiAlertCircle} className="mr-1.5" /> Rejected</span>;
          default:
            return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><SafeIcon icon={FiClock} className="mr-1.5" /> Pending</span>;
        }
      };

      return (
        <div>
          <div className="flex justify-end mb-6">
            <button onClick={handleAddNew} className="flex items-center space-x-2 bg-yellow-500 text-white px-5 py-2.5 rounded-lg hover:bg-yellow-600 transition-colors shadow">
              <SafeIcon icon={FiPlus} />
              <span>Post New Job</span>
            </button>
          </div>
          {loading ? ( <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div></div>) : 
          jobs.length === 0 ? (
            <div className="text-center bg-white border border-gray-200 rounded-lg py-16">
              <h3 className="text-xl font-medium text-gray-900 mb-2">No job postings yet!</h3>
              <p className="text-gray-600">Click "Post New Job" to add one.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <motion.div key={job.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between hover:shadow-md transition-shadow">
                  <div className="flex-1 mb-4 sm:mb-0">
                     <div className="flex items-center space-x-4">
                        <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                        {getStatusChip(job.status)}
                    </div>
                    <p className="text-sm text-gray-600">For: {job.profiles?.name || 'N/A'}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => handleEdit(job)} className="p-2 text-yellow-500 hover:text-yellow-600 rounded-md hover:bg-yellow-50 transition-colors"><SafeIcon icon={FiEdit2} /></button>
                    <button onClick={() => handleDelete(job.id)} className="p-2 text-red-600 hover:text-red-700 rounded-md hover:bg-red-50 transition-colors"><SafeIcon icon={FiTrash2} /></button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          {showFormModal && <JobFormModal job={editingJob} onClose={() => setShowFormModal(false)} onSuccess={fetchUserJobs} />}
        </div>
      );
    };

    export default UserJobsTab;